import {
  ETypeMainHead,
  CTypeCardView,
  FUNERAL_SIDO,
  DTypeBreadCrumb,
  FUNERAL_SIGUNGU,
  BTypeBeneficiaryCTA,
  colors,
  BTypeMap,
  FullWidthButton,
  useCrematoriumInquiryModal,
  getCrematoriumPhoneByCompanyname,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = crematoriums
    .filter((p) => targetSidos.includes(String(p.sido)))
    .map((crema) => ({
      params: {
        companyname: crema.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const companyname = params?.companyname;
  let crematorium;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    crematorium = crematoriums.find(
      (crema) => crema.companyname === companyname
    );
    if (crematorium) {
      name = crematorium.companyname;

      district = crematorium.address.slice(0, 2);
      if (crematorium.address.split(" ")[0].length == 4)
        district = crematorium.address[0] + crematorium.address[2];
      description = `${
        companyname || ""
      }의 화장 비용을 전액 감면 받기 위하여 기초생활수급자임을 증명하는 서류가 필요합니다. 수급자 증명서는 전국 시군구 또는 읍면동 주민센터에서 발급 가능하며, 무인민원발급기(본인에 한함)와 정부24 웹사이트에서 또한 발급 가능합니다.`;
    }
  }

  return {
    props: {
      defaultCrema: crematorium,
      pageDescription: description,
      pageKeywords: `${crematorium?.companyname}, ${crematorium?.companyname} 기초생활수급자, ${district} 기초생활수급자 장례, 장제비, 장제급여, 기초생활수급자 장례 얼마,`,
      pageOgTitle: `${crematorium?.companyname} 기초생활 보호 대상자를 위한 화장 비용 안내 | 서울경기인천장례정보포털`,
      pageOgDescription: description,
      pageOgImage: crematorium?.images[0].url || "",
      hasError: !crematorium,
    },
  };
};

export default function BeneficiaryCrematoriumPage({
  defaultCrema,
  pageOgTitle,
}: {
  defaultCrema: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionCrematoriums = crematoriums.filter((p) =>
    targetSidos.includes(String(p.sido))
  );

  /** 문의 모달의 개장·이장 / 고인 확인·방문이 거는 번호. 원본에 없으면 null 이고 고이로 폴백한다. */
  const facilityPhone = getCrematoriumPhoneByCompanyname(
    defaultCrema.companyname
  );
  const { openInquiryModal } = useCrematoriumInquiryModal({
    eventDomain: "metro",
    eventPath: "benef_crema",
  });
  const carouselItems = regionCrematoriums.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.metropolitan-funeral.co.kr/benefit/hwajang/${res.companyname}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionCrematoriums.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionCrematoriums.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <DTypeBreadCrumb
        mainColor={regionColor}
        firstItem={{
          text: `수도권 기초생활수급자 장례헬프센터`,
          href: `/benefit/`,
          event: {
            domain: "metro",
            path: "benef_crema",
            trigger_and_target: "br_crumb_benef",
          },
        }}
        goiHomeEvent={{
          domain: "metro",
          path: "benef_crema",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <ETypeMainHead
        mainColor={regionColor}
        defaultParlor={defaultCrema}
        title={`${defaultCrema.companyname}`}
        managed_type={defaultCrema.managed_type}
        address={defaultCrema.address}
        isHwajang
        event={{
          domain: "metro",
          path: "benef_crema",
          trigger_and_target: "uv_mth_req",
        }}
      />

      <Stack padding="20px 20px 40px">
        <BTypeBeneficiaryCTA mainColor={regionColor} />
      </Stack>
      <Stack
        className="body_16_m"
        style={{
          background: colors.gray50,
          padding: "12px 14px",
          margin: "0 20px",
        }}
      >
        기초생활수급자는 화장 비용이 전액 감면됩니다.
      </Stack>

      <Stack padding="40px 20px 32px" gap="4px">
        <Box className="subtitle_18_b">필요 서류</Box>
        <Stack gap="12px">
          <Box className="body_14_m" component="p">
            화장 비용을 전액 감면 받기 위하여 기초생활수급자임을 증명하는 서류가
            필요합니다.
          </Box>
          <Box className="subtitle_14_b" component="p">
            필요 서류 : 국민기초생활보장수급자 증명서
          </Box>
          <Box className="body_14_m" component="p">
            수급자 증명서는 전국 시군구 또는 읍면동 주민센터에서 발급 가능하며,
            무인민원발급기(본인에 한함)와 정부24 웹사이트에서 또한 발급
            가능합니다.
          </Box>
          <Box className="body_14_m" component="p">
            기초생활수급자라면 위의 2가지 혜택을 받을 수 있습니다.{" "}
            <b className="subtitle_14_b">
              이해하기 어려운 점이 있거나 더 자세하게 알고 싶은 것이 있다면,
              바로 상담을 진행하세요.
            </b>
          </Box>
        </Stack>
      </Stack>

      <Stack padding="20px">
        <FullWidthButton
          text="바로 상담하기"
          onClick={() => openInquiryModal(facilityPhone)}
          isGoiCall
          backgroundColor={regionColor.c300}
          event={{
            domain: "metro",
            path: "benef_crema",
            trigger_and_target: "goi_call",
          }}
        />
      </Stack>

      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>

      <Stack padding="40px 20px" marginBottom="40px" gap="8px">
        <BTypeMap defaultParlor={defaultCrema} />
      </Stack>

      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>

      <Stack padding="0px 20px" marginBottom="40px" gap="8px">
        <h3 className="button_16_sb">서울 · 경기 · 인천 지역 화장장</h3>
        {regionCrematoriums.map((res) => (
          <CTypeCardView
            sigungu={String(res.sigungu)}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/benefit/hwajang/${res.companyname}/`}
            descriptions={["기초생활수급자 무료"]}
            sido={String(res.sido)}
            imgSrc={res.images[0].url ? res.images[0].url : ""}
            event={{
              domain: "metro",
              path: "benef_crema",
              trigger_and_target: "uv_fr_crema_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
